import React, { useMemo, useState } from "react";
import { CalendarClock, Clock, Flame, Sun } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { EmptyState, InsightCard, InsightPill, SectionHeading, SegmentedToggle } from "../components/Primitives";
import { DAY_ORDER, deriveHeatmap, mergePlayPatterns } from "../utils/derive";

/**
 * Section B — Play rhythm (§4.3). The signature element of the tab: everything
 * else on the page stays quiet so this can carry the boldness.
 *
 * 7 rows (Mon–Sun) x 24 hour columns. Intensity is that day's sessions_per_month
 * painted only across the day's preferred_hours; hours outside a window sit at
 * zero rather than being smoothed into their neighbours.
 */

const SOURCES = [
  { value: "casino", label: "Casino" },
  { value: "sports", label: "Sports" },
  { value: "both", label: "Both" },
];

/**
 * Five discrete steps rather than a continuous alpha — the underlying number is
 * a small integer (2 to 4 sessions a month here), so banding it reads honestly
 * and keeps the classes static for Tailwind to find.
 */
const HEAT_STEPS = [
  "bg-muted",
  "bg-primary/20",
  "bg-primary/40",
  "bg-primary/70",
  "bg-primary",
];

const heatStep = (value, peak) => {
  if (!value) return 0;
  return Math.max(1, Math.min(4, Math.ceil((value / peak) * 4)));
};

const hourLabel = (hour) => `${String(hour).padStart(2, "0")}:00`;

/** "12:00-14:00" and "13:00-14:00" -> "12:00–14:00" style display. */
const prettyWindow = (window) => String(window).replace("-", "–");

/**
 * The three sentences beside the heatmap, read off the same grid the operator is
 * looking at rather than hard-coded, so they stay true for any player.
 */
function buildRhythmInsights(grid) {
  if (!grid) return [];

  const busiest = Math.max(...grid.rows.map((row) => row.sessions));
  const busiestDays = grid.rows.filter((row) => row.sessions === busiest);

  // The window that recurs across the most days, weighted by that day's sessions.
  const windowWeight = new Map();
  grid.rows.forEach((row) => {
    row.windows.forEach((window) => {
      windowWeight.set(window, (windowWeight.get(window) || 0) + row.sessions);
    });
  });
  const topWindow = [...windowWeight.entries()].sort((a, b) => b[1] - a[1])[0];

  const insights = [];

  if (topWindow && busiestDays.length) {
    const dayNames = busiestDays.map((row) => fullDay(row.day));
    insights.push({
      icon: Flame,
      tone: "accent",
      text: `Peak windows: ${listPhrase(dayNames)}, ${prettyWindow(topWindow[0])}.`,
    });
  }

  // A midday window on working days is worth calling out — it changes when a
  // CRM message or a reward will actually be seen. Overlapping windows collapse
  // into the one span they cover, so the sentence stays readable.
  const weekdayRows = grid.rows.filter((row) => !["saturday", "sunday"].includes(row.day));
  const middayBounds = weekdayRows.reduce(
    (bounds, row) =>
      row.windows.reduce((acc, window) => {
        const [start, end] = String(window).split("-").map((time) => Number(time.split(":")[0]));
        if (start < 11 || start > 13) return acc;
        return { from: Math.min(acc.from, start), to: Math.max(acc.to, end) };
      }, bounds),
    { from: 24, to: 0 }
  );
  if (middayBounds.from < middayBounds.to) {
    insights.push({
      icon: Sun,
      tone: "neutral",
      text: `Lunchtime player on weekdays (${hourLabel(middayBounds.from)}–${hourLabel(middayBounds.to)}).`,
    });
  }

  if (busiestDays.length) {
    insights.push({
      icon: CalendarClock,
      tone: "neutral",
      text: `Most active day: ${fullDay(busiestDays[0].day)}, ${busiestDays[0].sessions} sessions a month.`,
    });
  }

  return insights;
}

const FULL_DAYS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};
const fullDay = (day) => FULL_DAYS[day] || day;

const listPhrase = (items) =>
  items.length <= 1 ? items[0] || "" : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

function Heatmap({ grid }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="relative">
      {/* Hour scale */}
      <div className="mb-1.5 flex items-center gap-2 pl-8 pr-[76px]">
        <div className="flex flex-1 gap-[3px]">
          {Array.from({ length: 24 }, (_, hour) => (
            <span
              key={hour}
              className="flex-1 text-center text-xs leading-none text-muted-foreground"
              aria-hidden="true"
            >
              {hour % 3 === 0 ? String(hour).padStart(2, "0") : ""}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-[3px]">
        {grid.rows.map((row) => (
          <div key={row.day} className="flex items-center gap-2">
            <span className="w-6 shrink-0 text-xs font-medium text-muted-foreground">{row.label}</span>

            <div className="flex flex-1 gap-[3px]">
              {row.hours.map((value, hour) => {
                const step = heatStep(value, grid.peak);
                const isHovered = hovered && hovered.day === row.day && hovered.hour === hour;
                return (
                  <button
                    key={hour}
                    type="button"
                    tabIndex={-1}
                    aria-label={`${row.label} ${hourLabel(hour)}: ${value} sessions per month`}
                    onMouseEnter={() => setHovered({ day: row.day, label: row.label, hour, value })}
                    onMouseLeave={() => setHovered(null)}
                    className={cn(
                      "h-6 flex-1 rounded-[3px] transition-all duration-150",
                      HEAT_STEPS[step],
                      isHovered && "ring-2 ring-foreground ring-offset-1 ring-offset-card"
                    )}
                  />
                );
              })}
            </div>

            {/* Row-end annotation: sessions per month for this day. */}
            <span className="w-[68px] shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              <span className="font-semibold text-foreground">{row.sessions}</span> / mo
            </span>
          </div>
        ))}
      </div>

      {/* Intensity legend */}
      <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3">
        <p className="text-xs text-muted-foreground">
          Cell shade shows sessions a month in that hour band; blank hours are outside this player&rsquo;s windows.
        </p>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span>Quiet</span>
          {HEAT_STEPS.map((step, index) => (
            <span key={step} className={cn("h-2.5 w-4 rounded-[2px]", step)} aria-hidden="true" title={`${index}`} />
          ))}
          <span>Peak</span>
        </div>
      </div>

      {/* Single floating readout, rather than 168 individual tooltip instances. */}
      {hovered && (
        <div className="pointer-events-none absolute left-8 top-0 z-10 -translate-y-full rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md">
          <span className="font-medium">
            {hovered.label} {hourLabel(hovered.hour)}–{hourLabel((hovered.hour + 1) % 24)}
          </span>
          <span className="ml-2 tabular-nums text-muted-foreground">
            {hovered.value ? `${hovered.value} sessions / month` : "outside preferred hours"}
          </span>
        </div>
      )}
    </div>
  );
}

export function PlayRhythm({ profile, loading }) {
  const [source, setSource] = useState("both");

  const grid = useMemo(() => {
    const casino = profile?.casino?.play_patterns;
    const sports = profile?.sports?.play_patterns;
    if (source === "casino") return deriveHeatmap(casino);
    if (source === "sports") return deriveHeatmap(sports);
    return deriveHeatmap(mergePlayPatterns(casino, sports));
  }, [profile, source]);

  const insights = useMemo(() => buildRhythmInsights(grid), [grid]);

  const totalSessions = grid ? grid.rows.reduce((sum, row) => sum + row.sessions, 0) : 0;

  return (
    <section>
      <SectionHeading
        id="pi-rhythm"
        eyebrow="Section B"
        title="Play rhythm"
        description="When this player is actually at the table — the window to reach them in."
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <InsightCard
          className="xl:col-span-8"
          title="Weekly pattern"
          subtitle={grid ? `${totalSessions} sessions a month across ${DAY_ORDER.length} days` : undefined}
          control={
            <SegmentedToggle ariaLabel="Heatmap product" options={SOURCES} value={source} onChange={setSource} />
          }
        >
          {loading ? (
            <div className="space-y-[3px]">
              {DAY_ORDER.map((day) => (
                <div key={day} className="flex items-center gap-2">
                  <Skeleton className="h-3 w-6" />
                  <Skeleton className="h-6 flex-1" />
                  <Skeleton className="h-3 w-[68px]" />
                </div>
              ))}
            </div>
          ) : !grid ? (
            <EmptyState
              minHeight={228}
              message="Not enough history yet — a play rhythm builds after a few sessions."
            />
          ) : (
            <Heatmap grid={grid} />
          )}
        </InsightCard>

        <InsightCard className="xl:col-span-4" title="What the pattern says">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : !insights.length ? (
            <EmptyState minHeight={132} />
          ) : (
            <div className="space-y-2">
              {insights.map((insight) => (
                <InsightPill key={insight.text} icon={insight.icon} tone={insight.tone}>
                  {insight.text}
                </InsightPill>
              ))}
              <div className="flex items-start gap-2 rounded-md border border-dashed px-3 py-2">
                <Clock className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <p className="text-xs leading-snug text-muted-foreground">
                  Sessions a month come from the rolling session counter and are shown as recorded — they are not
                  divided into any other metric on this page.
                </p>
              </div>
            </div>
          )}
        </InsightCard>
      </div>
    </section>
  );
}

